import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PackageOpen, RotateCcw, Hotel as HotelIcon, Check, AlertTriangle, ChevronDown, ChevronUp, Plus, Minus, X } from "lucide-react";
import PdaLayout from "./PdaLayout";
import { beep, primeAudio } from "@/lib/pdaSounds";
import { usePdaScan } from "@/hooks/usePdaScan";
import usePdaScenarioHint from "@/hooks/usePdaScenarioHint";
import useLiveProductBreakdown from "@/hooks/useLiveProductBreakdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Receiving has two flavours:
 *   - normal : scan dirty linen → mark every RFID at_warehouse. No hotel.
 *   - rewash : choose a hotel first → scan dirty linen (or skip and create
 *              an empty rewash order). Backend creates a pending order.
 */
export default function PdaReceiving({ onLogout }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("normal"); // "normal" | "rewash"

  // Hotel selection (rewash only)
  const [hotels, setHotels] = useState([]);
  const [hotelLoading, setHotelLoading] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null); // { id, name, suburb, ... }
  const [hotelSearch, setHotelSearch] = useState("");

  // Scan session — set of unique RFIDs we've accumulated this session.
  const [rfids, setRfids] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Manual (chipless) items — rewash only. product_id -> {name, qty}
  const [manualItems, setManualItems] = useState({});
  const [products, setProducts] = useState([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productSearch, setProductSearch] = useState("");

  // Auto-switch RFID power profile with the mode — rewash gets its own
  // scenario (slot 7 in the wrapper) so it can run a different power.
  usePdaScenarioHint(mode === "rewash" ? "Rewash" : "Receiving");
  useEffect(() => { primeAudio(); }, []);

  // Live product breakdown so the operator sees "12 sheets / 6 towels" while
  // scanning rather than a wall of EPC codes (user's explicit preference).
  const { breakdown, unknown: liveUnknown } = useLiveProductBreakdown(rfids, { throttleMs: 200 });

  // Last burst feedback — "+N" flashes as soon as the operator releases the
  // scan key, so they know instantly how many came in without submitting.
  const [lastBurst, setLastBurst] = useState(null); // { added, total }

  // Load hotels on demand when switching to rewash. Each Hotel row is its
  // own selectable entry — a customer that owns multiple hotels appears as
  // separate rows, so the operator picks the exact destination (address +
  // coords) the rewash order belongs to.
  useEffect(() => {
    if (mode !== "rewash" || hotels.length > 0) return;
    setHotelLoading(true);
    axios.get(`${API}/hotels`)
      .then((res) => {
        const rows = (res.data || [])
          .map((h) => ({
            id: h.id,
            name: h.hotel_name || h.owner_email || "Hotel",
            owner_email: h.owner_email,
            suburb: h.suburb || "",
            hotel_address: h.hotel_address || "",
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setHotels(rows);
      })
      .catch(() => toast.error("Couldn't load hotels"))
      .finally(() => setHotelLoading(false));
  }, [mode, hotels.length]);

  // Load the products ASSIGNED to the selected hotel (manual-add picker).
  useEffect(() => {
    setManualItems({});
    setShowProductPicker(false);
    if (!selectedHotel) { setProducts([]); return; }
    axios.get(`${API}/hotels/${selectedHotel.id}/assigned-products`)
      .then((res) => setProducts(res.data.products || []))
      .catch(() => setProducts([]));
  }, [selectedHotel]);

  // Mirror of the rfids Set as a ref — lets us synchronously detect whether
  // an incoming batch contained any truly new RFIDs (so we only beep on
  // newly-added tags, not every read of a tag that's already in the session).
  const rfidsRef = useRef(new Set());

  const clearRfids = useCallback(() => {
    rfidsRef.current = new Set();
    setRfids(new Set());
    setLastBurst(null);
  }, []);

  // RFID intake (live)
  const onEpcs = useCallback((epcs) => {
    if (!epcs || epcs.length === 0) return;
    let added = 0;
    for (const e of epcs) {
      if (!rfidsRef.current.has(e)) { rfidsRef.current.add(e); added++; }
    }
    setLastBurst({ added, total: rfidsRef.current.size });
    if (added > 0) {
      setRfids(new Set(rfidsRef.current));
      beep.tick();
    }
  }, []);
  usePdaScan(onEpcs, { enabled: true });

  const manualTotal = useMemo(
    () => Object.values(manualItems).reduce((s, m) => s + m.qty, 0),
    [manualItems]
  );

  const setManualQty = (pid, name, delta) => {
    setManualItems((prev) => {
      const cur = prev[pid]?.qty || 0;
      const qty = cur + delta;
      const next = { ...prev };
      if (qty <= 0) delete next[pid];
      else next[pid] = { name, qty };
      return next;
    });
  };

  const canSubmit = useMemo(() => {
    if (mode === "normal") return rfids.size > 0;
    // rewash: hotel required; allow zero RFIDs (create empty rewash order)
    return !!selectedHotel;
  }, [mode, rfids.size, selectedHotel]);

  const onSubmit = async () => {
    if (submitting || !canSubmit) return;
    setSubmitting(true);
    try {
      if (mode === "normal") {
        const res = await axios.post(`${API}/linen/receive`, { rfid_codes: Array.from(rfids) });
        beep.complete();
        toast.success(`Received ${res.data.received_count} item(s)${res.data.unknown_count ? ` · ${res.data.unknown_count} unknown` : ""}`);
        clearRfids();
      } else {
        const manual_items = Object.entries(manualItems).map(([product_id, m]) => ({ product_id, quantity: m.qty }));
        await axios.post(`${API}/linen/rewash`, {
          hotel_id: selectedHotel.id,
          rfid_codes: Array.from(rfids),
          manual_items,
        });
        beep.complete();
        const parts = [];
        if (rfids.size) parts.push(`${rfids.size} scanned`);
        if (manualTotal) parts.push(`${manualTotal} manual`);
        toast.success(`Rewash order created (${parts.join(" + ") || "empty"})`);
        clearRfids();
        setManualItems({});
        setSelectedHotel(null);
      }
    } catch (e) {
      beep.error();
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredHotels = useMemo(() => {
    if (!hotelSearch.trim()) return hotels;
    const q = hotelSearch.toLowerCase();
    return hotels.filter((h) => h.name.toLowerCase().includes(q) || (h.owner_email || "").toLowerCase().includes(q) || (h.suburb || "").toLowerCase().includes(q));
  }, [hotels, hotelSearch]);

  return (
    <PdaLayout
      title="Receiving"
      subtitle={mode === "normal" ? "Dirty linen → warehouse" : "Rewash flow"}
      onBack={() => navigate("/pda/menu")}
      onLogout={onLogout}
      footer={
        <button
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          data-testid="pda-receiving-submit"
          className={`w-full h-12 rounded-md font-bold flex items-center justify-center gap-2 disabled:opacity-40 ${
            mode === "rewash" ? "bg-sky-600 text-white" : "bg-emerald-600 text-white"
          }`}
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> :
            mode === "normal" ? `Receive ${rfids.size} item(s)` :
            rfids.size + manualTotal === 0 ? "Create empty rewash" :
            `Rewash ${rfids.size + manualTotal} item(s)${manualTotal ? ` (${manualTotal} manual)` : ""}`
          }
        </button>
      }
    >
      {/* Mode tabs */}
      <div className="flex rounded-md border bg-white mb-3">
        <button
          onClick={() => { setMode("normal"); setSelectedHotel(null); setManualItems({}); setShowProductPicker(false); }}
          data-testid="pda-recv-mode-normal"
          className={`flex-1 py-2 text-sm font-bold rounded-l-md ${mode === "normal" ? "bg-amber-400 text-neutral-900" : "text-neutral-600"}`}
        >
          Normal
        </button>
        <button
          onClick={() => setMode("rewash")}
          data-testid="pda-recv-mode-rewash"
          className={`flex-1 py-2 text-sm font-bold rounded-r-md ${mode === "rewash" ? "bg-amber-400 text-neutral-900" : "text-neutral-600"}`}
        >
          Rewash
        </button>
      </div>

      {/* Rewash hotel picker */}
      {mode === "rewash" && (
        <div className="mb-3 rounded-md border bg-white">
          <div className="px-3 py-2 border-b flex items-center gap-2">
            <HotelIcon className="h-4 w-4 text-neutral-500" />
            <span className="text-xs font-bold">Hotel</span>
            {selectedHotel ? (
              <span className="ml-auto text-sm font-display font-bold truncate">{selectedHotel.name}</span>
            ) : (
              <span className="ml-auto text-xs text-rose-600">Required</span>
            )}
          </div>
          {!selectedHotel && (
            <>
              {hotelLoading ? (
                <div className="px-3 py-3 text-xs text-neutral-500"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-2" />Loading…</div>
              ) : (
                <>
                  <input
                    type="text"
                    value={hotelSearch}
                    onChange={(e) => setHotelSearch(e.target.value)}
                    data-testid="pda-recv-hotel-search"
                    placeholder="Search hotels…"
                    className="w-full px-3 py-2 border-b text-sm focus:outline-none"
                  />
                  <div className="max-h-56 overflow-y-auto">
                    {filteredHotels.map((h) => (
                      <button
                        key={h.id}
                        onClick={() => setSelectedHotel(h)}
                        data-testid={`pda-recv-hotel-${h.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b last:border-b-0"
                      >
                        <div className="text-sm font-medium truncate">{h.name}</div>
                        {h.suburb ? <div className="text-[10px] text-neutral-600 truncate">{h.suburb}</div> : null}
                        {h.owner_email ? <div className="text-[10px] text-neutral-500 truncate">{h.owner_email}</div> : null}
                      </button>
                    ))}
                    {filteredHotels.length === 0 ? (
                      <div className="px-3 py-3 text-xs text-neutral-500">No matches</div>
                    ) : null}
                  </div>
                </>
              )}
            </>
          )}
          {selectedHotel && (
            <button
              onClick={() => setSelectedHotel(null)}
              data-testid="pda-recv-hotel-change"
              className="px-3 py-2 text-xs text-amber-700 underline w-full text-left"
            >
              Change hotel
            </button>
          )}
        </div>
      )}

      {/* Scan stats */}
      <div className="rounded-md border bg-white px-3 py-4 mb-3 text-center">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">Scanned this session</div>
        <div className="font-display font-bold text-5xl text-neutral-900 mt-1" data-testid="pda-recv-count">{rfids.size}</div>
        {lastBurst && (
          <div
            className={`mt-1 text-sm font-bold ${lastBurst.added > 0 ? "text-emerald-600" : "text-neutral-400"}`}
            data-testid="pda-recv-last-burst"
          >
            {lastBurst.added > 0 ? `+${lastBurst.added} new this scan` : "No new tags (all already scanned)"}
          </div>
        )}
        <div className="text-xs text-neutral-500 mt-1">Press the middle key to scan</div>
        {rfids.size > 0 ? (
          <button onClick={clearRfids} data-testid="pda-recv-clear" className="mt-2 text-xs text-rose-700 underline flex items-center gap-1 mx-auto">
            <RotateCcw className="h-3 w-3" /> Clear
          </button>
        ) : null}
      </div>

      {/* Manual (chipless) items — rewash only, needs a hotel */}
      {mode === "rewash" && selectedHotel && (
        <div className="rounded-md border bg-white mb-3" data-testid="pda-recv-manual">
          <div className="px-3 py-2 border-b flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-neutral-600">Manual items (no chip)</span>
            <button
              onClick={() => { setShowProductPicker((v) => !v); setProductSearch(""); }}
              data-testid="pda-recv-manual-add"
              className="text-[11px] font-bold text-sky-700 flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" /> Add product
            </button>
          </div>
          {showProductPicker && (
            <div className="border-b">
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                data-testid="pda-recv-manual-search"
                placeholder="Search products…"
                className="w-full px-3 py-2 border-b text-sm focus:outline-none"
              />
              <div className="max-h-44 overflow-y-auto">
                {products.length === 0 ? (
                  <div className="px-3 py-2 text-[11px] text-neutral-500">No products assigned to this hotel.</div>
                ) : products
                  .filter((p) => !productSearch.trim() || (p.name || "").toLowerCase().includes(productSearch.toLowerCase()))
                  .map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setManualQty(p.id, p.name, 1); setShowProductPicker(false); }}
                      data-testid={`pda-recv-manual-product-${p.id}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 border-b last:border-b-0"
                    >
                      {p.name}
                    </button>
                  ))}
              </div>
            </div>
          )}
          {Object.keys(manualItems).length === 0 ? (
            !showProductPicker && (
              <div className="px-3 py-2 text-[11px] text-neutral-400">
                For items without an RFID chip — add product + quantity by hand.
              </div>
            )
          ) : (
            <div className="divide-y">
              {Object.entries(manualItems).map(([pid, m]) => (
                <div key={pid} className="px-3 py-2 flex items-center gap-2" data-testid={`pda-recv-manual-row-${pid}`}>
                  <span className="text-sm font-medium flex-1 truncate">{m.name}</span>
                  <button
                    onClick={() => setManualQty(pid, m.name, -1)}
                    data-testid={`pda-recv-manual-minus-${pid}`}
                    className="h-7 w-7 rounded border flex items-center justify-center active:bg-neutral-100"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="font-mono font-bold text-sm w-8 text-center">{m.qty}</span>
                  <button
                    onClick={() => setManualQty(pid, m.name, 1)}
                    data-testid={`pda-recv-manual-plus-${pid}`}
                    className="h-7 w-7 rounded border flex items-center justify-center active:bg-neutral-100"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setManualQty(pid, m.name, -m.qty)}
                    data-testid={`pda-recv-manual-remove-${pid}`}
                    className="h-7 w-7 rounded flex items-center justify-center text-rose-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {breakdown.length > 0 && (
        <div className="rounded-md border bg-white mb-2" data-testid="pda-recv-breakdown">
          <div className="px-3 py-2 border-b text-xs uppercase tracking-wider text-neutral-600">Live by product</div>
          <div className="divide-y">
            {breakdown.map((b) => (
              <div key={b.product_id} className="px-3 py-2 flex items-center justify-between">
                <span className="text-sm font-medium">{b.product_name}</span>
                <span className="font-mono tabular-nums font-bold text-sm">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {liveUnknown.length > 0 && (
        <RecvUnknownPanel unknown={liveUnknown} />
      )}
    </PdaLayout>
  );
}

function RecvUnknownPanel({ unknown }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border bg-amber-50 border-amber-200" data-testid="pda-recv-unknown">
      <button onClick={() => setOpen((v) => !v)} className="w-full px-3 py-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-900">
          <AlertTriangle className="h-3.5 w-3.5" /> Unknown tags · {unknown.length}
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-amber-900" /> : <ChevronDown className="h-3.5 w-3.5 text-amber-900" />}
      </button>
      {open && (
        <div className="max-h-40 overflow-y-auto divide-y divide-amber-200 border-t border-amber-200">
          {unknown.map((r) => (
            <div key={r} className="px-3 py-1.5 text-[11px] font-mono">{r}</div>
          ))}
        </div>
      )}
    </div>
  );
}
